import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { createSelectField, Layout, ScreenshotGrid } from '@roc/client-portal-shared/components';
import { LoginLogoImg, useBodyBackgroundColor, useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { DateField, NumberFormat, PrintableAreaRef, TextField, VerticalBarLoader } from '@roc/ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '../../../../apps/client-portal-public/src/app/hooks';
import SignatureCanvas from 'react-signature-canvas';
import { pdf } from '@react-pdf/renderer';

import React, { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import { downloadDocument, formatDate, getPdfBlobUrl, isNotBlank, citizenshipStatusOptions, isNil } from '@roc/feature-utils';
import { format, isValid } from 'date-fns';
import { ArrowBack, ArrowForward, CheckCircle } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { ProgressBar } from './components/progressBar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { VerticalLinearStepper } from './components/verticalLinearStepper';
import { TitleComp } from './components/titleComp';
import { PrimaryAddress } from './components/primaryAddress';

import 'swiper/swiper.min.css'
import 'swiper/modules/pagination/pagination.min.css'
import { useParams } from 'react-router';
import { DecodedParams } from 'libs/client-portal-shared/src/app/modules/creditBackground/stores/requestCreditAuthStore';
import { CreditAuthorizationSignatureStep } from './components/creditAuthorizationSignatureStep';
import { BACKGROUND_COMPLETED, WAITING_AUTHORIZATION } from '../borrowers/utils/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    containerWrapper: {
      padding: "0 16px",
      marginTop: 0,
    },
    paper: {
      padding: theme.spacing(2),
    },
    root: {
      width: '100%',
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    sigCanvas: {
      width: "100%",
      height: 200,
      background: "#f7f7f7",
      border: "1px solid #aaa",
      borderRadius: 16,
    }
  })
);



function getSteps() {
  //return ['Personal Information', 'Payment', 'Consent Agreement', 'Signature'];
  //return ['Personal Information', 'Consent Agreement', 'Signature'];
  return ['Your Information', 'Agree & Sign'];
}

const CreditScoreNoImpactAlert = observer(() => {
  const { isSmallScreen } = useBorrowerPortalMediaQueries();
  return (
    <Alert severity='info' icon={false} style={{
      borderRadius: 16
    }}>
      <Box display={"flex"} flexDirection={"row"} alignItems={'center'}>
        {
          isSmallScreen ? null :
            <Box fontSize={92}>
              ✅
            </Box>
        }
        <Box flexGrow={1} fontSize={18} ml={1}>
          <Box fontSize={24} mb={1}><strong>{isSmallScreen ? '✅ ' : ''}No impact to your credit score!</strong></Box>
          You're authorizing a soft credit inquiry, which means it won't affect your credit score like a hard inquiry would. This inquiry will only be visible to you, and will not be seen by other companies who are pulling your credit.
        </Box>
      </Box>
    </Alert>
  )
})

const Step1_content = observer(({
  disableNameFields = false,
  setNextDisabled,
}: {
  disableNameFields: boolean,
  setNextDisabled?: (val: boolean) => void;
}) => {
  const classes = useStyles();
  const { publicCreditAuthorizationStore } = useStore();
  const creditAuthorizationStore = publicCreditAuthorizationStore.personalInformationFormStore;
  const formFields = creditAuthorizationStore.form.fields;

  const manageDate = (fieldName, date) => {
    let formattedDate = null;
    if (isValid(date)) {
      formattedDate = format(date, 'MM/dd/yyyy');
    }
    creditAuthorizationStore.onFieldChange(fieldName, formattedDate);
  };

  useEffect(() => {
    setNextDisabled(false)
  }, [])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={4}>
              <CreditScoreNoImpactAlert />
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Personal Information" />
              <Box mt={2} mb={4} fontSize={18}>
                The following information is needed to begin the process of determining your identity and finding your credit report.
              </Box>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="firstName"
                required
                variant="outlined"
                label="First Name"
                value={formFields.firstName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'firstName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.firstName.error)}
                helperText={formFields.firstName.error}
                fullWidth
                disabled={disableNameFields}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="lastName"
                required
                variant="outlined"
                label="Last Name"
                value={formFields.lastName.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'lastName',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.lastName.error)}
                helperText={formFields.lastName.error}
                fullWidth
                disabled={disableNameFields}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateField
                testId="dateOfBirth"
                required
                inputVariant="outlined"
                format="MM/dd/yyyy"
                label="Date of Birth"
                value={formFields.dateOfBirth.value}
                onChange={date => manageDate('dateOfBirth', date)}
                disableFuture={true}
                fullWidth
                error={isNotBlank(formFields.dateOfBirth.error)}
                helperText={formFields.dateOfBirth.error}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {createSelectField({
                store: publicCreditAuthorizationStore.personalInformationFormStore,
                testId: 'citizenshipStatus',
                fieldName: 'citizenshipStatus',
                label: 'Citizenship Status',
                options: citizenshipStatusOptions,
                placeholder: 'Make Selection',
                standaloneLabel: false
              })}
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                customInput={TextField}
                format="###-##-####"
                mask="_"
                variant="outlined"
                label="SSN"
                testId="ssn"
                value={formFields.ssn.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'ssn',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.ssn.error)}
                helperText={formFields.ssn.error}
                fullWidth
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Primary Address" />
            </Box>
          </Grid>
          <PrimaryAddress store={creditAuthorizationStore} />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Contact Information" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="emailAddress"
                required
                variant="outlined"
                label="Email Address"
                value={formFields.emailAddress.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'emailAddress',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.emailAddress.error)}
                helperText={formFields.emailAddress.error}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                testId="phoneNumber"
                customInput={TextField}
                format="###-###-####"
                mask="_"
                variant="outlined"
                label="Cell Phone"
                value={formFields.phoneNumber.value}
                fullWidth
                required={true}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'phoneNumber',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.phoneNumber.error)}
                helperText={formFields.phoneNumber.error}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
})


const Step2_content = observer(({
  setNextDisabled,
}: {
  setNextDisabled?: (val: boolean) => void;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const [addressSameAsPrimary, setAddressSameAsPrimary] = useState(false);

  useEffect(() => {
    setNextDisabled(false)
  }, [])

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Payment" />
              <Box mt={2}>
                <Alert severity="info">
                  We charge a $100 fee to cover the cost of processing and retrieving your credit report.
                </Alert>
              </Box>
            </Box>
          </Grid>
          <CreditCardForm />
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Billing Address" />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={3}>
              <FormControlLabel
                value={addressSameAsPrimary}
                control={<Checkbox color="primary" />}
                label="Billing address is the same as primary address."
                labelPlacement="end"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setAddressSameAsPrimary(e.target.checked)
                }}
              />
            </Box>
          </Grid>
          <PrimaryAddress store={creditAuthorizationStore} disabled={addressSameAsPrimary} />
        </Grid>
      </Grid>
    </Box>
  )
})

const VerificationChip = ({
  title,
  color,
  icon,
}: {
  title: string,
  color: 'red' | 'orange' | 'green',
  icon?: React.ReactNode,
}) => {

  return (
    <Typography style={{
      color,
      margin: '4px 0px 0px 0px',
      padding: '2px 8px',
      border: '1px solid',
      borderRadius: 24,
      display: 'flex',
      alignItems: 'center',
      width: 'fit-content',
    }} variant='body1'>
      {icon}{title}
    </Typography>
  )
}

const Step4_content = observer(({
  setNextDisabled,
  isActive,
}: {
  setNextDisabled?: (val: boolean) => void;
  isActive: boolean;
}) => {
  const classes = useStyles();
  const { creditAuthorizationStore } = useStore();
  const formFields = creditAuthorizationStore.form.fields;
  const { isSmallScreen } = useBorrowerPortalMediaQueries();

  const [emailVerifyStep, setEmailVerifyStep] = useState<undefined | 'NOT_VERIFIED' | 'VERIFICATION_PENDING' | 'VERIFIED'>('NOT_VERIFIED');
  const [cellPhoneVerifyStep, setCellPhoneVerifyStep] = useState<undefined | 'NOT_VERIFIED' | 'VERIFICATION_PENDING' | 'VERIFIED'>('NOT_VERIFIED');

  const sigCanvas = useRef(null);

  useEffect(() => {
    if (isActive) {
      setNextDisabled(emailVerifyStep != 'VERIFIED' || cellPhoneVerifyStep != 'VERIFIED');
    } else {
      setNextDisabled(false);
    }
  }, [emailVerifyStep, cellPhoneVerifyStep, isActive])

  useEffect(() => {
    if (formFields.emailAddress.value) {
      setEmailVerifyStep('NOT_VERIFIED');
    } else {
      setEmailVerifyStep(undefined);
    }
  }, [formFields.emailAddress.value]);

  useEffect(() => {
    if (formFields.cellPhone.value) {
      setCellPhoneVerifyStep('NOT_VERIFIED');
    } else {
      setCellPhoneVerifyStep(undefined);
    }
  }, [formFields.cellPhone.value]);

  const EmailVerificationStep = (
    <>
      {
        emailVerifyStep == 'NOT_VERIFIED' &&
        <VerificationChip title='Not Verified' color='red' />
      }
      {
        emailVerifyStep == 'VERIFICATION_PENDING' &&
        <VerificationChip title='Verification Pending' color='orange' />
      }
      {
        emailVerifyStep == 'VERIFIED' &&
        <VerificationChip title='Verified' color='green' icon={<CheckCircle style={{
          marginRight: 8
        }} />} />
      }
    </>
  )

  const CellPhoneVerificationStep = (
    <>
      {
        cellPhoneVerifyStep == 'NOT_VERIFIED' &&
        <VerificationChip title='Not Verified' color='red' />
      }
      {
        cellPhoneVerifyStep == 'VERIFICATION_PENDING' &&
        <VerificationChip title='Verification Pending' color='orange' />
      }
      {
        cellPhoneVerifyStep == 'VERIFIED' &&
        <VerificationChip title='Verified' color='green' icon={<CheckCircle style={{
          marginRight: 8
        }} />} />
      }
    </>
  )

  return (
    <Box pb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box mb={3}>
              <TitleComp title="Email" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                testId="emailAddress"
                required
                variant="outlined"
                label="Email Address"
                value={formFields.emailAddress.value}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'emailAddress',
                    e.target.value
                  )
                }
                error={isNotBlank(formFields.emailAddress.error)}
                helperText={formFields.emailAddress.error}
                fullWidth
                disabled={emailVerifyStep == 'VERIFIED'}
              />
              {
                isSmallScreen ? EmailVerificationStep : null
              }
            </Grid>
            {
              isSmallScreen ? null :
                <Grid item xs={12} sm={6} style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {EmailVerificationStep}
                </Grid>
            }
            {
              emailVerifyStep == 'NOT_VERIFIED' &&
              <>
                <Grid item xs={12} sm={6}>
                  <Button variant='outlined' color='primary' size='small' onClick={() => {
                    setEmailVerifyStep('VERIFICATION_PENDING')
                  }}>
                    Send Verification Code
                  </Button>
                </Grid>
              </>
            }
            {
              emailVerifyStep == 'VERIFICATION_PENDING' &&
              <>
                <Grid item xs={12} sm={6} style={{
                  display: 'flex'
                }}>
                  <div
                    style={{
                      flexGrow: 1,
                    }}>
                    <NumberFormat
                      testId="emailVerificationCode"
                      customInput={TextField}
                      format="#  #  #  #  #  #"
                      mask="_"
                      variant="outlined"
                      label="Email Verification Code"
                      fullWidth
                      required={true}
                    />
                    <Box mt={1} display={'flex'} justifyContent={'space-between'} width="100%" onClick={() => { }}>
                      <Button variant='outlined' color='primary' size='small' onClick={() => {
                        setEmailVerifyStep('VERIFIED')
                      }}>
                        Verify
                      </Button>
                      <Button size="small">Send code again</Button>
                    </Box>
                  </div>
                </Grid>
              </>
            }
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Cell Phone" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <NumberFormat
                testId="cellPhone"
                customInput={TextField}
                format="(###) ###-####"
                mask="_"
                variant="outlined"
                label="Cell Phone"
                value={formFields.cellPhone.value}
                fullWidth
                required={true}
                onChange={e =>
                  creditAuthorizationStore.onFieldChange(
                    'cellPhone',
                    e.target.value
                  )
                }
                disabled={cellPhoneVerifyStep == 'VERIFIED'}
              />
              {
                isSmallScreen ? CellPhoneVerificationStep : null
              }
            </Grid>
            {
              isSmallScreen ? null :
                <Grid item xs={12} sm={6} style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  {CellPhoneVerificationStep}
                </Grid>
            }
            {
              cellPhoneVerifyStep == 'NOT_VERIFIED' &&
              <>
                <Grid item xs={12} sm={6}>
                  <Button variant='outlined' color='primary' size='small' onClick={() => {
                    setCellPhoneVerifyStep('VERIFICATION_PENDING')
                  }}>
                    Send Verification Code
                  </Button>
                </Grid>
              </>
            }
            {
              cellPhoneVerifyStep == 'VERIFICATION_PENDING' &&
              <>
                <Grid item xs={12} sm={6} style={{
                  display: 'flex'
                }}>
                  <div
                    style={{
                      flexGrow: 1,
                    }}>
                    <NumberFormat
                      testId="cellPhoneVerificationCode"
                      customInput={TextField}
                      format="#  #  #  #  #  #"
                      mask="_"
                      variant="outlined"
                      label="Cell Phone Verification Code"
                      fullWidth
                      required={true}
                    />
                    <Box mt={1} display={'flex'} justifyContent={'space-between'} width="100%" onClick={() => { }}>
                      <Button variant='outlined' color='primary' size='small' onClick={() => {
                        setCellPhoneVerifyStep('VERIFIED')
                      }}>
                        Verify
                      </Button>
                      <Button size="small">Send code again</Button>
                    </Box>
                  </div>
                </Grid>
              </>
            }
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Grid xs={12}>
            <Box my={3}>
              <TitleComp title="Signature" />
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SignatureCanvas penColor='green'
                canvasProps={{ className: classes.sigCanvas }}
                ref={sigCanvas} />
              <Box display={'flex'} justifyContent={'space-between'} width="100%">
                <Button size="small" onClick={() => {
                  const dataURL = sigCanvas.current.toDataURL("image/png");
                  console.log(dataURL);
                  sigCanvas.current.clear();
                }}>Clear</Button>
                <Box>
                  {formatDate(new Date(), 'MM/dd/yyyy')}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
})

const CreditCardForm = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {/* Name on Card */}
        <TextField
          testId='cc_name'
          label="Name on Card"
          variant="outlined"
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12}>
        {/* Credit Card Number */}
        {/* <TextField
          testId='cc_number'
          label="Credit Card Number"
          variant="outlined"
          fullWidth
          placeholder="XXXX XXXX XXXX XXXX"
        /> */}
        <NumberFormat
          testId='cc_number'
          customInput={TextField}
          format="####  ####  ####  ####"
          placeholder="XXXX  XXXX  XXXX  XXXX"
          mask="_"
          required={true}
          variant="outlined"
          label="Credit / Debit Card Number"
          value={undefined}
          onChange={e => { }
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        {/* <TextField
          testId='cc_exp_date'
          label="Exp Date"
          variant="outlined"
          fullWidth
          placeholder="MM/YY"
        /> */}
        <DateField
          testId="cc_exp_date"
          required
          inputVariant="outlined"
          format="MM/yyyy"
          label="Exp Date"
          value={null}
          onChange={date => { }}
          disableFuture={true}
          fullWidth
          placeholder="MM/YY"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          testId='cc_cvv'
          label="Security Code"
          variant="outlined"
          fullWidth
          placeholder="CVV"
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          testId='cc_zip'
          label="ZIP Code"
          variant="outlined"
          fullWidth
          placeholder="XXXXX"
          required
        />
      </Grid>
    </Grid>
  );
};

export const CAF = observer(({ decodedParams, }: { decodedParams: DecodedParams, }) => {
  const classes = useStyles();
  const { publicCreditAuthorizationStore, requestCreditAuthStore } = useStore();
  const [active, setActive] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const { isSmallScreen, isLargeScreen } = useBorrowerPortalMediaQueries();
  const steps = getSteps();
  const swiperRef = useRef<any>(null);
  const screenshotRef = useRef<any>(null);
  const [invalidLink, setInvalidLink] = useState(false);
  const { requestStatus } = publicCreditAuthorizationStore;

  const [completed, setCompleted] = useState(false);

  useBodyBackgroundColor("#fff");

  useEffect(() => {
    if (!isNil(decodedParams.requestToken) && requestStatus && requestStatus !== WAITING_AUTHORIZATION && requestStatus !== BACKGROUND_COMPLETED) {
      setInvalidLink(true);
    }
  }, [requestStatus]);

  useEffect(() => {
    setPercentCompleted(active * (100 / steps.length));
  }, [active]);

  const handleSaveFormInformation = async () => {
    const formData = publicCreditAuthorizationStore.personalInformationFormStore.getFormValues();
    return await requestCreditAuthStore.saveFormInformation(formData, decodedParams, true, false);
  }

  useEffect(() => {
    publicCreditAuthorizationStore.resetStore();
    publicCreditAuthorizationStore.fetchInvalidSSN();
    if (!isNil(decodedParams.requestToken)) {
      flowResult(publicCreditAuthorizationStore.fetchBorrowerDetailsVerification(decodedParams.requestToken))
        .then(requestExists => {
          if (!requestExists) {
            setInvalidLink(true);
          }
        });
    }
  }, []);

  return (
    <div style={{ backgroundColor: 'white', minWidth: '100%' }}>
      <Layout maxWidth="lg" bodyContentBoxClass={classes.containerWrapper}>
        <Box mt={isLargeScreen ? 2 : 0}>
          <LoginLogoImg imgStyle={{
            maxWidth: 300
          }} />
        </Box>
        {invalidLink ? (
          <Box mt={4} textAlign={'center'}>
            <Typography variant="h4">Sorry, this link is no longer valid.</Typography>
          </Box>
        ) : (
          <>
            {
              completed ? null :
                <Box mb={4} textAlign={'left'}>
                  <Typography variant={isSmallScreen ? 'h4' : 'h3'}>
                    Credit Authorization Form
                  </Typography>
                  <Box>
                    <ProgressBar value={percentCompleted} />
                  </Box>
                </Box>
            }
            {
              completed ?
                <>
                  <Grid container spacing={2}>
                    <Box mt={3} width={'100%'}>
                      <Box textAlign={'center'} margin={'auto'} maxWidth={1000}>
                        <Box pb={2} fontSize={32} display={'flex'} alignItems={'center'} justifyContent={'center'} flexDirection={isSmallScreen ? 'column' : 'row'}>
                          <CheckCircle style={{
                            color: 'green',
                            fontSize: isSmallScreen ? 60 : 40,
                            marginRight: 8,
                          }} />
                          <div>
                            Your authorization has been successfully submitted!
                          </div>
                        </Box>
                        <Box pb={2} fontSize={20}>
                          A confirmation email with the signed form has been sent to you. Thank you!
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </>
                :
                <Grid container spacing={2}>
                  <Grid item xs zeroMinWidth>
                    <Swiper
                      spaceBetween={50}
                      slidesPerView={1}
                      onSlideChange={(swiper) => {
                        setActive(swiper.realIndex);
                      }}
                      onSwiper={(swiper) => {
                        (swiperRef.current = swiper)
                      }}
                      allowTouchMove={false} // Prevents user swiping if not needed
                    >
                      {steps.map((StepContent, index) => (
                        <SwiperSlide key={index} style={{
                          padding: '0 2px'
                        }}>
                          {
                            active == 0 && index === 0 && <Step1_content setNextDisabled={setNextDisabled} disableNameFields={publicCreditAuthorizationStore.disableNameFields} />
                          }
                          {/* {
                      active == 1 && index === 1 && <Step2_content setNextDisabled={setNextDisabled} />
                    } */}
                          {
                            active == 1 && index === 1 &&
                            <CreditAuthorizationSignatureStep
                              requestToken={requestCreditAuthStore.borrowerDetailsVerification?.requestToken}
                              onComplete={() => {
                                setCompleted(true);
                                setPercentCompleted(100);
                              }}
                            />
                          }
                          {/* {
                      active == 2 && index === 2 && <Step4_content setNextDisabled={setNextDisabled} isActive={active === index} />
                    } */}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <Grid item xs={12} sm={12}>
                      <Box mt={4} display={'flex'} justifyContent={active == 0 ? 'flex-end' : 'space-between'}>
                        {
                          active != 0 &&
                          <Button variant='outlined' color='default' startIcon={<ArrowBack />} size='large' onClick={() => {
                            swiperRef.current?.slidePrev();
                          }} style={{
                            padding: '16px 24px'
                          }}>
                            Back
                          </Button>
                        }
                        {
                          active != steps.length - 1 &&
                          <Button variant='contained' color='primary' endIcon={<ArrowForward />} size='large' onClick={async () => {
                            if (publicCreditAuthorizationStore.validateStep()) {
                              const isSaved = await handleSaveFormInformation();
                              if (isSaved) {
                                swiperRef.current?.slideNext();
                              }
                            }
                          }} disabled={nextDisabled} style={{
                            padding: '16px 24px'
                          }}>
                            Next
                          </Button>
                        }
                      </Box>
                    </Grid>
                  </Grid>
                  {
                    isLargeScreen &&
                    <Grid item style={{ height: '100%', width: '1px', background: '#ddd', margin: '0 24px', padding: 0 }}></Grid>
                  }
                  {
                    isLargeScreen &&
                    <Grid item style={{ width: 280 }}>
                      <VerticalLinearStepper active={active} steps={getSteps()} />
                    </Grid>
                  }
                </Grid>
            }
          </>
        )}
      </Layout>
    </div>
  );
});
